/**
 * Client-side mirror of the processor's mapping engine (showroompricing-file-processor
 * src/domain/mapping.ts) — used ONLY for the rule-builder's live preview against a sample file.
 * Keep in sync with the processor. Authoritative execution + validation still happen server-side
 * (canonical-preview + dry-run); this is for instant feedback while authoring a spec.
 */

const cell = (row, col) => (row[col] == null ? '' : String(row[col]));
const isBlank = (v) => String(v ?? '').trim() === '';

function toNumber(v) {
  const cleaned = String(v ?? '').replace(/[^0-9.-]/g, '');
  if (cleaned === '' || cleaned === '-' || cleaned === '.') return NaN;
  return Number(cleaned);
}

function applyTransforms(value, transforms) {
  let v = value;
  for (const t of transforms || []) {
    switch (t.op) {
      case 'trim': v = v.trim(); break;
      case 'upper': v = v.toUpperCase(); break;
      case 'lower': v = v.toLowerCase(); break;
      case 'substring': v = v.substring(t.start, t.end); break;
      case 'replace': v = v.split(t.find).join(t.with); break;
      case 'map': v = (t.table && t.table[v] != null) ? t.table[v] : v; break;
      default: break;
    }
  }
  return v;
}

function evalExpr(expr, row) {
  if ('col' in expr) return cell(row, expr.col);
  if ('const' in expr) return String(expr.const);
  if ('coalesce' in expr) {
    for (const e of expr.coalesce) {
      const v = evalExpr(e, row);
      if (!isBlank(v)) return v;
    }
    return '';
  }
  const nums = (list) => list.map((e) => toNumber(evalExpr(e, row)));
  let result;
  if ('add' in expr) result = nums(expr.add).reduce((a, b) => a + (Number.isNaN(b) ? 0 : b), 0);
  else if ('sub' in expr) {
    const parts = nums(expr.sub);
    const first = parts.length ? parts[0] : 0;
    result = parts.slice(1).reduce((a, b) => a - (Number.isNaN(b) ? 0 : b), Number.isNaN(first) ? 0 : first);
  } else if ('mul' in expr) result = nums(expr.mul).reduce((a, b) => a * (Number.isNaN(b) ? 0 : b), 1);
  else return '';
  return Number.isFinite(result) ? String(Number(result.toFixed(6))) : '';
}

function formatToday(fmt) {
  const d = new Date();
  const yyyy = String(d.getFullYear());
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return (fmt || 'YYYY-MM-DD').replace('YYYY', yyyy).replace('MM', mm).replace('DD', dd);
}

function resolveField(rule, row) {
  if ('const' in rule) return rule.const;
  if ('today' in rule) return formatToday(rule.today);
  if ('if' in rule) return resolveField(passesAll(rule.if, row) ? rule.then : (rule.else || { const: '' }), row);
  let value;
  if ('expr' in rule) {
    value = evalExpr(rule.expr, row);
  } else if ('concat' in rule) {
    const parts = rule.concat.map((c) => cell(row, c)).filter((p) => !isBlank(p));
    value = applyTransforms(parts.join(rule.delimiter || ''), rule.transform);
  } else {
    value = applyTransforms(cell(row, rule.from), rule.transform);
  }
  if (isBlank(value) && 'default' in rule && rule.default !== undefined) return rule.default;
  return value;
}

function passesFilter(f, row) {
  const v = cell(row, f.col);
  switch (f.op) {
    case 'nonEmpty': return !isBlank(v);
    case 'isEmpty': return isBlank(v);
    case 'nonZero': return toNumber(v) !== 0 && !Number.isNaN(toNumber(v));
    case 'nonEmptyNonZero': return !isBlank(v) && toNumber(v) !== 0 && !Number.isNaN(toNumber(v));
    case 'equals': return v === (f.value || '');
    case 'notEquals': return v !== (f.value || '');
    case 'dateNotFuture': { const t = Date.parse(v); return Number.isNaN(t) ? true : t <= Date.now(); }
    default: return true;
  }
}

function passesAll(filters, row) {
  return (filters || []).every((f) => passesFilter(f, row));
}

function applyFillDown(rows, cols) {
  if (!cols || !cols.length) return rows;
  const last = {};
  return rows.map((row) => {
    const out = { ...row };
    for (const c of cols) {
      if (isBlank(cell(out, c))) out[c] = last[c] || '';
      else last[c] = cell(out, c);
    }
    return out;
  });
}

export function applyMapping(spec, rows) {
  const prepared = applyFillDown(rows, spec.fillDown || []);
  const out = [];
  for (const row of prepared) {
    if (!passesAll(spec.rowFilters, row)) continue;
    for (const template of spec.outputs || []) {
      if (!passesAll(template.when, row)) continue;
      const mapped = {};
      for (const [field, rule] of Object.entries(template.fields || {})) {
        mapped[field] = resolveField(rule, row);
      }
      out.push(mapped);
    }
  }
  return out;
}

export function specColumns(spec) {
  const all = new Set();
  for (const t of spec.outputs || []) for (const f of Object.keys(t.fields || {})) all.add(f);
  if (spec.outputColumns && spec.outputColumns.length) {
    const ordered = [...spec.outputColumns];
    for (const c of all) if (!ordered.includes(c)) ordered.push(c);
    return ordered;
  }
  return [...all];
}
